import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const Courses = ({ homePage }) => {
  const [category, setCategory] = useState([]);
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategorySet, setIsCategorySet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showUpArrow, setShowUpArrow] = useState(false);

  const location = useLocation();
  const pathParts = location.pathname?.split("/");
  const activePath = pathParts[pathParts?.length - 1];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Scroll detection for mobile up arrow
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const coursesSection = document.getElementById("courses-display");
      if (coursesSection) {
        const rect = coursesSection.getBoundingClientRect();
        // Show arrow when courses section is visible and user has scrolled past categories
        setShowUpArrow(rect.top < window.innerHeight && window.scrollY > 200);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  useEffect(() => {
    if (category?.length > 0 && !isCategorySet) {
      setSelectedCategory(category[0]);
      setIsCategorySet(true);
    }
  }, [category, isCategorySet]);

  const handleCategoryClick = (clickedItem) => {
    localStorage.setItem("selectedCat", JSON.stringify(clickedItem));
    setSelectedCategory(clickedItem);

    // Mobile: scroll to courses section
    if (isMobile) {
      setTimeout(() => {
        const coursesSection = document.getElementById("courses-display");
        if (coursesSection) {
          coursesSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  // Handle up arrow click - scroll to categories
  const handleUpArrowClick = () => {
    const categoriesSection = document.getElementById("categories-section");
    if (categoriesSection) {
      categoriesSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      setShowUpArrow(false);
    }
  };

  useEffect(() => {
    const selectedCat = JSON.parse(localStorage.getItem("selectedCat"));
    if (selectedCat && !isCategorySet) {
      setSelectedCategory(selectedCat);
      setIsCategorySet(true);
    }
  }, [isCategorySet]);

  useEffect(() => {
    const getCategory = () => {
      setIsLoading(true);
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/category`)
        .then((response) => {
          setCategory(response?.data?.data?.categories);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setIsLoading(false);
        });
    };
    getCategory();
  }, []);

  const courseClickHandle = (subjectName, courseId) => {
    navigate(`/course-about/${subjectName}`, { state: { courseId } });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const categoryVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };

  const courseVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 },
    },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B02526]"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="relative py-16 lg:pb-24 lg:pt-6 bg-gradient-to-br from-gray-50 via-white to-blue-50"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 right-10 w-32 h-32 bg-blue-100 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-red-100 rounded-full opacity-40 animate-bounce"></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-yellow-100 rounded-full opacity-50"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div className="text-center mb-16" variants={itemVariants}>
          {/* Decorative line */}
          {/* <div className="flex items-center justify-center mb-8">
            <div className="h-1 w-20 bg-gradient-to-r from-transparent via-[#B02526] to-transparent"></div>
            <div className="mx-4 px-6 py-2 bg-[#B02526] text-white text-sm font-semibold rounded-full">
              COURSES
            </div>
            <div className="h-1 w-20 bg-gradient-to-r from-[#B02526] via-transparent to-transparent"></div>
          </div> */}

          {homePage && (
            <h1 className="text-4xl lg:text-6xl !text-[#183864] font-bold text-gray-900 mb-6 leading-tight">
              Courses
            </h1>
          )}
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our comprehensive course schedule across multiple campuses.
            Professional training designed to advance your career and skills.
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          id="categories-section"
          className="flex flex-col lg:flex-row gap-8 lg:gap-12"
          variants={itemVariants}
        >
          {/* Categories Sidebar */}
          <div className="w-full lg:w-1/3">
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 lg:sticky lg:top-[120px] lg:z-[50] max-h-max h-max overflow-y-auto"
              variants={itemVariants}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-2 h-8 bg-[#B02526] rounded-full mr-3"></div>
                Categories
              </h3>

              <div
                className={`space-y-3 pr-2 ${isMobile ? "" : "max-h-[calc(100vh-300px)] overflow-y-auto"}`}
                style={{
                  scrollbarWidth: isMobile ? "none" : "thin",
                  scrollbarColor: isMobile ? "transparent" : "#B02526 #f1f1f1",
                  padding: "12px 8px", // Increased padding to prevent cut-off
                }}
                onWheel={(e) => {
                  // Disable wheel handling on mobile since categories don't scroll
                  if (isMobile) return;

                  const categoriesElement = e.currentTarget;
                  const coursesElement = document.querySelector(
                    ".courses-scroll-container",
                  );

                  // Priority 1: Handle courses scroll first
                  if (coursesElement) {
                    const {
                      scrollTop: coursesScrollTop,
                      scrollHeight: coursesScrollHeight,
                      clientHeight: coursesClientHeight,
                    } = coursesElement;
                    const coursesHasScroll =
                      coursesScrollHeight > coursesClientHeight;

                    if (coursesHasScroll) {
                      // If scrolling down and courses can still scroll down
                      if (
                        e.deltaY > 0 &&
                        coursesScrollTop <
                          coursesScrollHeight - coursesClientHeight
                      ) {
                        coursesElement.scrollTop += e.deltaY;
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }

                      // If scrolling up and courses can still scroll up
                      if (e.deltaY < 0 && coursesScrollTop > 0) {
                        coursesElement.scrollTop += e.deltaY;
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                    }
                  }

                  // Priority 2: Handle categories scroll after courses is done
                  const { scrollTop, scrollHeight, clientHeight } =
                    categoriesElement;
                  const categoriesHasScroll = scrollHeight > clientHeight;

                  if (categoriesHasScroll) {
                    // If scrolling down and categories can still scroll down
                    if (
                      e.deltaY > 0 &&
                      scrollTop < scrollHeight - clientHeight
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }

                    // If scrolling up and categories can still scroll up
                    if (e.deltaY < 0 && scrollTop > 0) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                  }

                  // Priority 3: Allow page scroll only after both courses and categories are done
                  // Let the event bubble up naturally for page scroll
                }}
              >
                <AnimatePresence>
                  {category?.map((items, index) => (
                    <motion.div
                      key={items._id}
                      variants={categoryVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                      className={`
                        cursor-pointer rounded-xl p-4 transition-all duration-300 border-2 mx-2
                        ${
                          selectedCategory?._id === items._id
                            ? "bg-gradient-to-r from-[#B02526] to-[#8B1E1F] text-white border-[#B02526] shadow-lg transform scale-105"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200 hover:border-[#B02526]/30"
                        }
                      `}
                      onClick={() => handleCategoryClick(items)}
                      whileHover={{
                        scale:
                          selectedCategory?._id === items._id ? 1.03 : 1.01,
                      }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        transformOrigin: "center center", // Ensure scaling from center
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm lg:text-base">
                          {items.name}
                        </span>
                        <div
                          className={`
                          w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                          ${
                            selectedCategory?._id === items._id
                              ? "bg-white/20 text-white"
                              : "bg-[#B02526]/10 text-[#B02526]"
                          }
                        `}
                        >
                          {items.courses?.length || 0}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          <div id="courses-display"></div>
          {/* Courses Display */}
          <div className="w-full lg:w-2/3">
            <motion.div
              id="courses-display"
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
              variants={itemVariants}
            >
              {/* Category Header - Fixed */}
              <div className="bg-gradient-to-r from-[#183864] to-[#2C5282] py-3 px-6 lg:p-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                  {selectedCategory?.name || "Select a Category"}
                </h2>
                <p className="text-blue-100">
                  {selectedCategory?.courses?.length || 0} courses available
                </p>
              </div>

              {/* Courses List */}
              <div
                className="courses-scroll-container py-3 px-6 max-h-max overflow-y-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#B02526 #f1f1f1",
                }}
                onWheel={(e) => {
                  const coursesElement = e.currentTarget;
                  const { scrollTop, scrollHeight, clientHeight } =
                    coursesElement;

                  // Check if courses has scrollable content
                  const coursesHasScroll = scrollHeight > clientHeight;

                  if (coursesHasScroll) {
                    // If scrolling down and courses can still scroll down
                    if (
                      e.deltaY > 0 &&
                      scrollTop < scrollHeight - clientHeight
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }

                    // If scrolling up and courses can still scroll up
                    if (e.deltaY < 0 && scrollTop > 0) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                  }

                  // If courses scroll is complete or has no scroll, allow bubbling to categories
                  // Do not stop propagation here - let it bubble up to categories handler
                }}
              >
                {selectedCategory?.courses &&
                selectedCategory.courses.length > 0 ? (
                  <div className="space-y-4">
                    <AnimatePresence mode="wait">
                      {selectedCategory.courses.map((course, index) => (
                        <motion.div
                          key={course._id}
                          variants={courseVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          transition={{ delay: index * 0.1 }}
                          className="group cursor-pointer bg-gray-50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-red-50 rounded-xl py-2 px-4 transition-all duration-300 border border-gray-200 hover:border-[#B02526]/30 hover:shadow-lg"
                          onClick={() =>
                            courseClickHandle(course.slug, course._id)
                          }
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-3">
                                <div className="w-3 h-3 bg-[#B02526] rounded-full mr-3 group-hover:animate-pulse"></div>
                                <h4 className="text-lg lg:text-xl font-bold text-gray-900 group-hover:text-[#B02526] transition-colors">
                                  {course.name}
                                </h4>
                              </div>
                              {course.description && (
                                <p className="text-gray-600 text-sm line-clamp-2 mb-1">
                                  {course.description
                                    .replace(/<[^>]*>/g, "")
                                    .substring(0, 80)}
                                  ...
                                </p>
                              )}
                            </div>
                            <div className="ml-4 flex items-center">
                              <div className="w-10 h-10 bg-[#B02526]/10 group-hover:bg-[#B02526] rounded-full flex items-center justify-center transition-all duration-300">
                                <svg
                                  className="w-5 h-5 text-[#B02526] group-hover:text-white transition-colors"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">
                      No courses available in this category
                    </p>
                  </div>
                )}

                {/* See All Courses Button */}
                {activePath !== "all-courses" &&
                  selectedCategory?.courses &&
                  selectedCategory.courses.length > 0 && (
                    <motion.div
                      className="mt-8 text-center"
                      variants={itemVariants}
                    >
                      <Link
                        to="/all-courses"
                        className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#B02526] to-[#8B1E1F]  hover:from-[#8B1E1F] hover:to-[#B02526] text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <span>View All Courses</span>
                        <svg
                          className="ml-2 w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </Link>
                    </motion.div>
                  )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Floating Up Arrow for Mobile */}
      <AnimatePresence>
        {isMobile && showUpArrow && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3 }}
            onClick={handleUpArrowClick}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-[#B02526] to-[#8B1E1F] text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{ zIndex: 999 }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        /* Webkit Scrollbar Styles */
        div::-webkit-scrollbar {
          width: 6px;
        }

        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        div::-webkit-scrollbar-thumb {
          background: #b02526;
          border-radius: 3px;
        }

        div::-webkit-scrollbar-thumb:hover {
          background: #8b1e1f;
        }

        /* Smooth scrolling behavior */
        .smooth-scroll {
          scroll-behavior: smooth;
        }

        /* Focus styles for better accessibility */
        div[tabindex]:focus {
          outline: 2px solid #b02526;
          outline-offset: 2px;
        }
      `}</style>
    </motion.div>
  );
};

export default Courses;
